import type {
  ExportFormat,
  Paginated,
  WordRecordView,
  WordbookListResult,
  WordStatus,
} from '@en3/shared';
import { api } from './client';
import { toQuery } from './content.api';
import { getAccessToken } from './token';

/**
 * 生词本接口（阶段 4）。
 *
 * `queryKeys` 的失效策略在这里说明清楚，避免各处乱 invalidate：
 * - 任何写操作（新增/修改/删除）都要失效 `['wordbook']` 与 `['me']`
 *   （首页与个人中心的"生词数 / 今日待复习"会跟着变）；
 * - 复习提交后额外失效 `['review']`。
 */

export interface WordbookListParams {
  page?: number;
  pageSize?: number;
  status?: WordStatus;
  due?: boolean;
  q?: string;
  tag?: string;
  articleId?: string;
  sort?: 'recent' | 'due' | 'familiarity' | 'alpha';
}

export interface WordRecordCreateBody {
  wordId?: string;
  surface?: string;
  status?: WordStatus;
  articleId?: string;
  contextSnippet?: string;
  note?: string;
  tags?: string[];
  countLookup?: boolean;
}

export const wordbookApi = {
  list: (params: WordbookListParams = {}) =>
    api.get<WordbookListResult>(`/api/wordbook${toQuery({ ...params, due: params.due ? 1 : undefined })}`),

  create: (body: WordRecordCreateBody) => api.post<{ record: WordRecordView }>('/api/word-records', body),

  update: (
    id: string,
    body: { status?: WordStatus; note?: string | null; tags?: string[]; familiarity?: number },
  ) => api.patch<{ record: WordRecordView }>(`/api/word-records/${id}`, body),

  remove: (id: string) => api.delete<{ deleted: boolean }>(`/api/word-records/${id}`),

  /** 分页取全部（导出与统计用；上限 100/页，见 shared 的 PAGINATION）。 */
  all: (params: WordbookListParams = {}) =>
    api.get<Paginated<WordRecordView>>(`/api/wordbook${toQuery({ ...params, pageSize: 100 })}`),

  /**
   * 导出下载。
   *
   * 后端返回的是**文件流**而不是 JSON 信封，因此不能走 `api.get`（它会尝试解信封）。
   * 这里直接用 `fetch` + Blob：同源请求会自动带 Cookie，鉴权靠内存里的 accessToken，
   * 所以仍然手动加一次 Authorization 头。
   */
  async download(format: ExportFormat, params: { status?: WordStatus; due?: boolean } = {}): Promise<void> {
    // `token.ts` 用静态 import：动态 import 在这里没有意义（client.ts 早就静态引了它，
    // 打包器只会给出 INEFFECTIVE_DYNAMIC_IMPORT 警告，分不出独立 chunk）。
    const token = getAccessToken();
    const res = await fetch(`/api/export/wordbook${toQuery({ format, ...params, due: params.due ? 1 : undefined })}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      credentials: 'include',
    });

    if (!res.ok) {
      // 失败时后端返回的是 JSON 信封，这里尽量把它转成可展示的中文
      let message = '导出失败，请稍后重试';
      try {
        const payload = (await res.json()) as { error?: { message?: string } };
        if (payload.error?.message) message = payload.error.message;
      } catch {
        // 不是 JSON：保留默认文案
      }
      throw new Error(message);
    }

    const blob = await res.blob();
    const disposition = res.headers.get('Content-Disposition') ?? '';
    const matched = /filename="?([^";]+)"?/.exec(disposition);
    const filename = matched?.[1] ?? `en3-wordbook.${format}`;

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename;
    document.body.append(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);
  },
};
